import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Mail, ArrowRight, CheckCircle, Award, Users, Clock, Heart, Target, Handshake } from "lucide-react";

const VKB_LOGO_URL = "https://customer-assets.emergentagent.com/job_ingenieur-agency/artifacts/7oos7e3e_VKBayern_logo.svg.png";
const MANSFELD_IMAGE = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/cl8hh574_ChatGPT%20Image%2026.%20Ma%CC%88rz%202026%2C%2011_31_35.png";
// Team photo
const MANSFELD_TEAM = "https://customer-assets.emergentagent.com/job_3d119e6f-14f2-49d0-b48b-21438dbeafee/artifacts/v6u41d5v_ChatGPT%20Image%2026.%20Ma%CC%88rz%202026%2C%2014_15_58.png";

export default function UeberMichPage() {
  const values = [
    {
      icon: Heart,
      title: "Persönlich",
      description: "Persönliche Betreuung mit einem festen Ansprechpartner – immer erreichbar, wenn Sie mich brauchen."
    },
    {
      icon: Award,
      title: "Verlässlich",
      description: "Sie können sich auf meine Zusagen verlassen. Was ich verspreche, halte ich."
    },
    {
      icon: Target,
      title: "Leidenschaftlich",
      description: "Die Absicherung von Architekten und Ingenieuren ist nicht nur mein Beruf, sondern meine Berufung."
    }
  ];

  const qualifications = [
    "Spezialisiert auf Architekten und Ingenieure",
    "Langjährige Erfahrung in der Versicherungsbranche",
    "Konzern Versicherungskammer Bayern",
    "Persönliche, individuelle Beratung",
    "Regelmäßige Fortbildungen und Schulungen",
    "Direkter Draht zu Fachabteilungen"
  ];

  return (
    <div data-testid="ueber-mich-page" className="pt-8">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-[#003B70] to-[#002855] overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 50% 50%, rgba(255,255,255,0.3) 1px, transparent 1px)`,
            backgroundSize: '30px 30px'
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sky-300 font-semibold text-sm uppercase tracking-wider">Ihr Berater</span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
                Jens-Oliver<br />Mansfeld
              </h1>
              <p className="text-xl text-white/80 mb-8 leading-relaxed">
                Fachagentur für Architekten und Ingenieure.<br />
                Persönlich. Verlässlich. Leidenschaftlich.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/kontakt">
                  <Button className="bg-white text-[#003B70] hover:bg-slate-100 px-6 py-5">
                    Kontakt aufnehmen
                  </Button>
                </Link>
                <a href="tel:+493033937950">
                  <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 px-6 py-5">
                    <Phone className="mr-2 h-4 w-4" />
                    Anrufen
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="relative block mt-4 lg:mt-0">
              <div className="absolute -inset-4 bg-gradient-to-r from-sky-400/20 to-blue-600/20 rounded-2xl blur-3xl" />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl max-w-sm mx-auto lg:max-w-none">
                <img 
                  src={MANSFELD_IMAGE}
                  alt="Jens-Oliver Mansfeld"
                  className="w-full mx-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Image & Contact Card */}
            <div className="space-y-8">
              {/* Beratung Image */}
              <div className="flex justify-center">
                <div className="relative w-64 h-64 rounded-2xl overflow-hidden border-4 border-white/20 shadow-2xl">
                  <img 
                    src={MANSFELD_TEAM}
                    alt="Jens-Oliver Mansfeld - Beratung"
                    className="w-full h-full object-cover object-[30%_center]"
                  />
                </div>
              </div>
              
              {/* Contact Card */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#003B70] flex items-center justify-center">
                    <Phone className="h-5 w-5 text-white" />
                  </div>
                  Kontakt
                </h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <Phone className="h-5 w-5 text-[#003B70] mt-1" />
                    <div>
                      <p className="text-sm text-slate-500">Telefon</p>
                      <a href="tel:+493033937950" className="font-semibold text-slate-900 hover:text-[#003B70] transition-colors">
                        +49 30 33937950
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Mail className="h-5 w-5 text-[#003B70] mt-1" />
                    <div>
                      <p className="text-sm text-slate-500">E-Mail</p>
                      <a href="mailto:info@mansfeld-versicherungen.de" className="font-semibold text-slate-900 hover:text-[#003B70] transition-colors text-sm break-all">
                        info@mansfeld-versicherungen.de
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="h-5 w-5 text-[#003B70] mt-1" />
                    <div>
                      <p className="text-sm text-slate-500">Erreichbarkeit</p>
                      <p className="font-semibold text-slate-900">Mo – Fr: 09:00 – 18:00 Uhr</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <img 
                    src={VKB_LOGO_URL}
                    alt="Versicherungskammer Bayern"
                    className="h-20 w-auto"
                  />
                  <p className="text-sm text-slate-500 mt-3">
                    Versicherungskammer Bayern
                  </p>
                </div>
              </div>
            </div>

            {/* Text Content */}
            <div>
              <span className="text-[#003B70] font-semibold text-sm uppercase tracking-wider">Über mich</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-6">
                Ihr Partner für sichere Planung
              </h2>
              
              <div className="prose prose-lg text-slate-600 mb-10">
                <p className="leading-relaxed">
                  Mit über 25 Jahren Erfahrung in der Versicherungsbranche bin ich Ihr Experte für 
                  die Absicherung von Architekten und Ingenieuren. Mein Fokus liegt auf drei 
                  zentralen Leistungsbausteinen:
                </p>
                <ul className="list-none pl-0 space-y-2 my-4">
                  <li className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="h-5 w-5 text-[#003B70] flex-shrink-0" />
                    <strong>Berufshaftpflicht</strong> – Kernstück Ihrer Existenzsicherung
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="h-5 w-5 text-[#003B70] flex-shrink-0" />
                    <strong>Cyberschutz</strong> – Schutz in der digitalen Welt
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="h-5 w-5 text-[#003B70] flex-shrink-0" />
                    <strong>Betriebliche Altersvorsorge</strong> – Für Ihre Zukunft
                  </li>
                </ul>
                <p className="leading-relaxed">
                  Ich kenne die Risiken Ihrer Branche und biete Ihnen passgenaue Beratung mit 
                  Weitsicht und Expertise. Die maßgeschneiderten Lösungen der Versicherungskammer Bayern 
                  bilden dabei die Grundlage für Ihre optimale Absicherung.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-slate-900">Meine Arbeitsweise:</strong> Strukturiert und 
                  auf Ihre individuellen Bedürfnisse zugeschnitten.
                </p>
              </div>

              <div className="mb-10">
                <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Handshake className="h-6 w-6 text-[#003B70]" />
                  Qualifikationen & Expertise
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {qualifications.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <CheckCircle className="h-5 w-5 text-[#003B70] flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#003B70] font-semibold text-sm uppercase tracking-wider">Meine Werte</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">
              Persönlich. Verlässlich. Leidenschaftlich.
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Diese drei Werte leiten mich in meiner täglichen Arbeit.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="group text-center bg-white p-10 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#003B70] to-[#002855] flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  <value.icon className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#003B70]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Lassen Sie uns sprechen
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Persönlich, telefonisch oder digital – wie es für Sie am besten passt.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/kontakt">
              <Button className="bg-white text-[#003B70] hover:bg-slate-100 px-8 py-5">
                <Mail className="mr-2 h-5 w-5" />
                Kontakt aufnehmen
              </Button>
            </Link>
            <a href="tel:+493033937950">
              <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 px-8 py-5">
                <Phone className="mr-2 h-5 w-5" />
                +49 30 33937950
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
